import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import type {
  InvoiceCreateInput,
  InvoiceUpdateInput,
  InvoiceLineCreateInput,
  TipoFactura,
  EstadoFactura,
} from '@/lib/invoicing/types';

// =============================================
// Line calculation helpers
// =============================================

function calculateLineBase(line: InvoiceLineCreateInput): number {
  const descuento = line.descuento_porcentaje ?? 0;
  return +(line.cantidad * line.precio_unitario * (1 - descuento / 100)).toFixed(2);
}

function calculateLineTaxes(base: number, line: InvoiceLineCreateInput) {
  const porcentajeIva = line.porcentaje_iva ?? 21;
  const cuotaIva = +(base * porcentajeIva / 100).toFixed(2);
  const recargoCuota = line.recargo_porcentaje
    ? +(base * line.recargo_porcentaje / 100).toFixed(2)
    : 0;
  const retencionCuota = line.retencion_porcentaje
    ? +(base * line.retencion_porcentaje / 100).toFixed(2)
    : 0;

  return { cuotaIva, recargoCuota, retencionCuota };
}

function calculateTotalsFromLines(lines: InvoiceLineCreateInput[]) {
  let baseImponible = 0;
  let totalImpuestos = 0;
  let totalRetenciones = 0;
  let totalSuplidos = 0;

  for (const line of lines) {
    const base = calculateLineBase(line);
    const { cuotaIva, recargoCuota, retencionCuota } = calculateLineTaxes(base, line);

    if (line.es_suplido) {
      totalSuplidos += base;
    } else {
      baseImponible += base;
      totalImpuestos += cuotaIva + recargoCuota;
      totalRetenciones += retencionCuota;
    }
  }

  return {
    base_imponible: +baseImponible.toFixed(2),
    total_impuestos: +totalImpuestos.toFixed(2),
    total_retenciones: +totalRetenciones.toFixed(2),
    total_suplidos: +totalSuplidos.toFixed(2),
    total_factura: +(baseImponible + totalImpuestos - totalRetenciones + totalSuplidos).toFixed(2),
  };
}

// =============================================
// Generate next invoice number from series
// =============================================

async function generateNextNumber(serieId: string): Promise<{ numero: string; nextSeq: number }> {
  const { data: serie, error } = await supabaseAdmin
    .from('invoice_series')
    .select('*')
    .eq('id', serieId)
    .single();

  if (error || !serie) {
    throw new Error('Serie no encontrada');
  }

  if (!serie.activa) {
    throw new Error('La serie no está activa');
  }

  const seq = serie.siguiente_numero ?? 1;
  const prefijo = serie.prefijo ?? '';
  const paddedNum = String(seq).padStart(5, '0');
  const numero = `${prefijo}${paddedNum}`;

  return { numero, nextSeq: seq + 1 };
}

// =============================================
// Handler
// =============================================

export default withAuth(async (req, res, user) => {
  try {
    switch (req.method) {
      case 'GET':
        return handleGet(req, res);
      case 'POST':
        return handleCreate(req, res, user.id);
      case 'PUT':
        return handleUpdate(req, res);
      case 'DELETE':
        return handleDelete(req, res);
      default:
        return res.status(405).json({ error: 'Método no permitido' });
    }
  } catch (error: any) {
    console.error('[API invoices] Unexpected error:', error);
    return res.status(500).json({ error: error.message || 'Error interno del servidor' });
  }
});

// =============================================
// GET: List invoices with filters + pagination
// =============================================

async function handleGet(req: NextApiRequest, res: NextApiResponse) {
  const {
    empresa_id,
    estado,
    tipo,
    fecha_desde,
    fecha_hasta,
    cliente_nif,
    search,
    page = '1',
    limit = '20',
    sort_by = 'created_at',
    sort_dir = 'desc',
  } = req.query;

  if (!empresa_id) {
    return res.status(400).json({ error: 'empresa_id es requerido' });
  }

  const pageNum = Math.max(1, parseInt(page as string, 10));
  const limitNum = Math.min(100, Math.max(1, parseInt(limit as string, 10)));
  const offset = (pageNum - 1) * limitNum;

  let query = supabaseAdmin
    .from('invoices')
    .select('*', { count: 'exact' })
    .eq('empresa_id', empresa_id as string)
    .order(sort_by as string, { ascending: sort_dir === 'asc' })
    .range(offset, offset + limitNum - 1);

  if (estado) query = query.eq('estado', estado as string);
  if (tipo) query = query.eq('tipo', tipo as string);
  if (cliente_nif) query = query.eq('cliente_nif', cliente_nif as string);
  if (fecha_desde) query = query.gte('fecha_emision', fecha_desde as string);
  if (fecha_hasta) query = query.lte('fecha_emision', fecha_hasta as string);
  if (search) {
    query = query.or(
      `cliente_nombre.ilike.%${search}%,numero.ilike.%${search}%,cliente_nif.ilike.%${search}%`
    );
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('[API invoices] GET error:', error);
    return res.status(500).json({ error: `Error al listar facturas: ${error.message}` });
  }

  return res.status(200).json({
    data: data ?? [],
    total: count ?? 0,
    page: pageNum,
    limit: limitNum,
    pages: Math.ceil((count ?? 0) / limitNum),
  });
}

// =============================================
// POST: Create invoice (borrador) with lines
// =============================================

async function handleCreate(req: NextApiRequest, res: NextApiResponse, userId: string) {
  const body: InvoiceCreateInput = req.body;

  // Validate required fields
  if (!body.empresa_id) return res.status(400).json({ error: 'empresa_id es requerido' });
  if (!body.serie_id) return res.status(400).json({ error: 'serie_id es requerido' });
  if (!body.cliente_nombre) return res.status(400).json({ error: 'cliente_nombre es requerido' });
  if (!body.cliente_nif) return res.status(400).json({ error: 'cliente_nif es requerido' });
  if (!body.lines || body.lines.length === 0) {
    return res.status(400).json({ error: 'Se requiere al menos una línea de factura' });
  }

  // Generate invoice number from series
  let numero: string;
  let nextSeq: number;
  try {
    const result = await generateNextNumber(body.serie_id);
    numero = result.numero;
    nextSeq = result.nextSeq;
  } catch (err: any) {
    return res.status(400).json({ error: err.message });
  }

  // Calculate totals from lines
  const totals = calculateTotalsFromLines(body.lines);

  // Insert invoice
  const invoiceData = {
    empresa_id: body.empresa_id,
    serie_id: body.serie_id,
    numero,
    tipo: body.tipo ?? 'ordinaria',
    estado: 'borrador' as EstadoFactura,
    cliente_nombre: body.cliente_nombre,
    cliente_nif: body.cliente_nif,
    cliente_direccion: body.cliente_direccion ?? null,
    cliente_email: body.cliente_email ?? null,
    cliente_telefono: body.cliente_telefono ?? null,
    fecha_emision: body.fecha_emision ?? new Date().toISOString().split('T')[0],
    fecha_vencimiento: body.fecha_vencimiento ?? null,
    fecha_operacion: body.fecha_operacion ?? null,
    moneda: body.moneda ?? 'EUR',
    tipo_cambio: body.tipo_cambio ?? 1,
    ...totals,
    tipo_operacion: body.tipo_operacion ?? 'nacional',
    clave_regimen: body.clave_regimen ?? 'general',
    factura_rectificada_id: body.factura_rectificada_id ?? null,
    motivo_rectificacion: body.motivo_rectificacion ?? null,
    es_recurrente: body.es_recurrente ?? false,
    recurrente_config: body.recurrente_config ?? null,
    notas: body.notas ?? null,
    notas_internas: body.notas_internas ?? null,
    created_by: userId,
  };

  const { data: invoice, error: invoiceError } = await supabaseAdmin
    .from('invoices')
    .insert(invoiceData)
    .select()
    .single();

  if (invoiceError) {
    console.error('[API invoices] Insert invoice error:', invoiceError);
    return res.status(500).json({ error: `Error al crear factura: ${invoiceError.message}` });
  }

  // Insert lines
  const linesData = body.lines.map((line, idx) => {
    const base = calculateLineBase(line);
    const { cuotaIva, recargoCuota, retencionCuota } = calculateLineTaxes(base, line);
    return {
      invoice_id: invoice.id,
      orden: line.orden ?? idx + 1,
      concepto: line.concepto,
      descripcion: line.descripcion ?? null,
      cantidad: line.cantidad,
      precio_unitario: line.precio_unitario,
      descuento_porcentaje: line.descuento_porcentaje ?? 0,
      base_linea: base,
      tipo_iva: line.tipo_iva ?? 'IVA_GENERAL',
      porcentaje_iva: line.porcentaje_iva ?? 21,
      cuota_iva: cuotaIva,
      recargo_porcentaje: line.recargo_porcentaje ?? null,
      recargo_cuota: recargoCuota || null,
      retencion_porcentaje: line.retencion_porcentaje ?? null,
      retencion_cuota: retencionCuota || null,
      es_suplido: line.es_suplido ?? false,
      suplido_nif_tercero: line.suplido_nif_tercero ?? null,
    };
  });

  const { error: linesError } = await supabaseAdmin.from('invoice_lines').insert(linesData);

  if (linesError) {
    console.error('[API invoices] Insert lines error:', linesError);
    // Rollback invoice
    await supabaseAdmin.from('invoices').delete().eq('id', invoice.id);
    return res.status(500).json({ error: `Error al crear líneas: ${linesError.message}` });
  }

  // Update series siguiente_numero
  await supabaseAdmin
    .from('invoice_series')
    .update({ siguiente_numero: nextSeq })
    .eq('id', body.serie_id);

  // Return invoice with lines
  const { data: fullInvoice } = await supabaseAdmin
    .from('invoices')
    .select('*')
    .eq('id', invoice.id)
    .single();

  const { data: lines } = await supabaseAdmin
    .from('invoice_lines')
    .select('*')
    .eq('invoice_id', invoice.id)
    .order('orden', { ascending: true });

  return res.status(201).json({
    success: true,
    data: { ...fullInvoice, lines: lines ?? [] },
  });
}

// =============================================
// PUT: Update borrador invoice
// =============================================

async function handleUpdate(req: NextApiRequest, res: NextApiResponse) {
  const { id, lines: newLines, ...updates } = req.body as InvoiceUpdateInput & {
    id: string;
    lines?: InvoiceLineCreateInput[];
  };

  if (!id) return res.status(400).json({ error: 'id es requerido' });

  // Verify invoice exists and is borrador
  const { data: existing, error: fetchError } = await supabaseAdmin
    .from('invoices')
    .select('id, estado')
    .eq('id', id)
    .single();

  if (fetchError || !existing) {
    return res.status(404).json({ error: 'Factura no encontrada' });
  }

  if (existing.estado !== 'borrador') {
    return res.status(400).json({ error: 'Solo se pueden editar facturas en estado borrador' });
  }

  // If new lines provided, recalculate totals
  let totals = {};
  if (newLines && newLines.length > 0) {
    totals = calculateTotalsFromLines(newLines);

    // Delete old lines and insert new ones
    await supabaseAdmin.from('invoice_lines').delete().eq('invoice_id', id);

    const linesData = newLines.map((line, idx) => {
      const base = calculateLineBase(line);
      const { cuotaIva, recargoCuota, retencionCuota } = calculateLineTaxes(base, line);
      return {
        invoice_id: id,
        orden: line.orden ?? idx + 1,
        concepto: line.concepto,
        descripcion: line.descripcion ?? null,
        cantidad: line.cantidad,
        precio_unitario: line.precio_unitario,
        descuento_porcentaje: line.descuento_porcentaje ?? 0,
        base_linea: base,
        tipo_iva: line.tipo_iva ?? 'IVA_GENERAL',
        porcentaje_iva: line.porcentaje_iva ?? 21,
        cuota_iva: cuotaIva,
        recargo_porcentaje: line.recargo_porcentaje ?? null,
        recargo_cuota: recargoCuota || null,
        retencion_porcentaje: line.retencion_porcentaje ?? null,
        retencion_cuota: retencionCuota || null,
        es_suplido: line.es_suplido ?? false,
        suplido_nif_tercero: line.suplido_nif_tercero ?? null,
      };
    });

    const { error: linesError } = await supabaseAdmin.from('invoice_lines').insert(linesData);
    if (linesError) {
      return res.status(500).json({ error: `Error al actualizar líneas: ${linesError.message}` });
    }
  }

  // Build update data - only include fields that were passed
  const updateData: Record<string, unknown> = { ...totals, updated_at: new Date().toISOString() };
  const allowedFields = [
    'tipo', 'cliente_nombre', 'cliente_nif', 'cliente_direccion', 'cliente_email',
    'cliente_telefono', 'fecha_emision', 'fecha_vencimiento', 'fecha_operacion',
    'moneda', 'tipo_cambio', 'tipo_operacion', 'clave_regimen',
    'es_recurrente', 'recurrente_config', 'notas', 'notas_internas',
  ];

  for (const field of allowedFields) {
    if ((updates as Record<string, unknown>)[field] !== undefined) {
      updateData[field] = (updates as Record<string, unknown>)[field];
    }
  }

  const { data, error } = await supabaseAdmin
    .from('invoices')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('[API invoices] PUT error:', error);
    return res.status(500).json({ error: `Error al actualizar factura: ${error.message}` });
  }

  // Fetch lines
  const { data: lines } = await supabaseAdmin
    .from('invoice_lines')
    .select('*')
    .eq('invoice_id', id)
    .order('orden', { ascending: true });

  return res.status(200).json({ success: true, data: { ...data, lines: lines ?? [] } });
}

// =============================================
// DELETE: Soft delete borrador invoice
// =============================================

async function handleDelete(req: NextApiRequest, res: NextApiResponse) {
  const { id } = req.body;

  if (!id) return res.status(400).json({ error: 'id es requerido' });

  // Verify invoice is borrador
  const { data: existing, error: fetchError } = await supabaseAdmin
    .from('invoices')
    .select('id, estado')
    .eq('id', id)
    .single();

  if (fetchError || !existing) {
    return res.status(404).json({ error: 'Factura no encontrada' });
  }

  if (existing.estado !== 'borrador') {
    return res.status(400).json({ error: 'Solo se pueden eliminar facturas en estado borrador' });
  }

  // Soft delete by setting estado to anulada
  const { error } = await supabaseAdmin
    .from('invoices')
    .update({ estado: 'anulada', updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) {
    console.error('[API invoices] DELETE error:', error);
    return res.status(500).json({ error: `Error al eliminar factura: ${error.message}` });
  }

  return res.status(200).json({ success: true, message: 'Factura eliminada (anulada)' });
}
